"use client";

/**
 * AlertCellNode — a single alert-step card shown inside the alert canvas.
 *
 *  - Top-left pill shows the alert-relative index (1-based), not notebook index.
 *  - Preview area prioritizes OUTPUT over code once the step has finished.
 *  - Footer is sparse: bottom-left shows the `send` pill only for alert-send
 *    steps (no "compute step" text for the rest), bottom-right is the expand
 *    chevron. The output-count counter is gone — the preview conveys the type.
 */

import { memo, useEffect, useMemo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import {
  Bell,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Cpu,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ICY, EMERALD } from "@/lib/alert-theme";
import {
  useAlertCellModal,
  type AlertCellModalData,
  type AlertCellOutput,
} from "./AlertCellModalContext";
import { AlertCellCarousel } from "./AlertCellCarousel";
import {
  isDashboardOutput,
  normalizeAlertOutputs,
  sortOutputsByVisualPriority,
} from "@/lib/alert-output-detection";
import { alertLog, summarizeOutput } from "@/lib/alert-display-log";
import { useAlertPublicView } from "@/components/alert-page/AlertPaywallContext";

const DEFAULT_NODE_MAX_HEIGHT = 340;

export interface AlertCellNodeData extends Omit<AlertCellModalData, "purpose"> {
  purpose: string;
  /** 2-line code preview (used as fallback mini-preview when no outputs yet). */
  codePreview: string;
  outputCount?: number;
  /** When true, the node's carousel freezes (used while the alert is running
   *  on the server so the canvas stops flipping cards mid-execution).      */
  carouselPaused?: boolean;
  /** True ONLY on the /alerts/[id] page. Enables carousel rotation (3s,
   *  staggered) and the hero-node right-column treatment. On /chat alert
   *  mode this stays false and the carousel renders static. */
  isAlertsRoute?: boolean;
  /** True for the last cell on /alerts when it has a dashboard output. The
   *  node is hoisted into its own right-hand column, widened, and its
   *  carousel is forced to show the dashboard (no rotation, ever). */
  isHeroNode?: boolean;
  /** True for the signal "Final Results" hero node. Clicking routes to the
   *  Dashboard tab (signal performance view) instead of opening the cell
   *  modal — the register card is a preview of that dashboard. */
  isSignalHero?: boolean;
  /** Visual size preset — "hero" renders larger (~720×600) with the
   *  dashboard preview filling the card. "default" is the normal
   *  380-wide node. */
  variant?: "default" | "hero";
  /** Rich markdown description of the cell ("**What it does**" +
   *  "**What it produces**"). Pre-generated by the backend at alert
   *  upload time and stored on the alert doc. When present, the
   *  carousel's Summary tab renders this instead of the short cell
   *  title. Null for cell 0 (setup) and for legacy alerts that haven't
   *  been backfilled yet. */
  descriptionMd?: string | null;
  /** When true, the node's carousel renders ONLY the dashboard view
   *  (no code / stdout / plotly / df tabs). Used by the hero "Final
   *  Results" node so the last cell reads as a clean dashboard card
   *  whether the caller is owner, subscriber, or delayed viewer. */
  forceDashboardOnly?: boolean;
  /** Viewer-state flags — used by the expand-click handler to decide
   *  whether to open the normal AlertCellModal (owner / subscriber) or
   *  to trigger the paywall instead (non-subscribed strangers). */
  viewerIsOwner?: boolean;
  viewerIsSubscribed?: boolean;
  viewMode?: string;
  // React Flow requires node data to be assignable to Record<string, unknown>.
  [key: string]: unknown;
}

function StatusIcon({
  status,
  viewMode,
}: {
  status: AlertCellNodeData["status"];
  viewMode?: string;
}) {
  if (status === "success") {
    return (
      <span
        className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-400/15 text-emerald-300"
        aria-label="Ran cleanly"
        title="Ran cleanly"
      >
        <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} />
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-400/15 text-red-300"
        aria-label="Errored"
        title="Errored"
      >
        <AlertTriangle className="w-3 h-3" strokeWidth={2.4} />
      </span>
    );
  }
  if (status === "running") {
    return (
      <Loader2
        className={`shrink-0 w-3.5 h-3.5 animate-spin ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`}
        aria-label="Running"
      />
    );
  }
  return (
    <span
      className="shrink-0 w-1.5 h-1.5 rounded-full bg-slate-400/60"
      aria-label={status === "pending" ? "Pending" : "Queued"}
    />
  );
}

function AlertCellNodeComponent({ data, selected }: NodeProps) {
  const d = data as unknown as AlertCellNodeData;
  const isRunning = d.status === "running";
  const isSuccess = d.status === "success";
  const isError = d.status === "error";
  const isHero = d.variant === "hero" || Boolean(d.isHeroNode);
  const isAlertsRoute = Boolean(d.isAlertsRoute);
  const isSignalHero = Boolean(d.isSignalHero);
  const { openModal } = useAlertCellModal();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Expand-click paywall gate (rewritten 2026-04-21).
  //   - Owners OR paid users → normal modal path (openModal).
  //   - Free / signed-out on /alerts → open paywall instead.
  //
  // `useAlertPublicView()` is provider-safe: on /chat alert-mode there's
  // no provider so canGoLive defaults to true, meaning the expand modal
  // always opens (historical behaviour preserved for /chat).
  const viewerIsOwner = Boolean(d.viewerIsOwner);
  const { canGoLive, openPaywall: publicViewOpenPaywall } = useAlertPublicView();
  const needsPaywall = isAlertsRoute && !viewerIsOwner && !canGoLive;
  const openPaywall = () => publicViewOpenPaywall("live");

  // Sort outputs by visual priority so the FIRST output slide in both the
  // carousel and the modal is always the strongest visual (dashboard →
  // plotly → chart-html → image → table → stdout). The same ordering is
  // used everywhere, which keeps the canvas + modal consistent.
  const sortedOutputs = useMemo(
    () =>
      sortOutputsByVisualPriority(
        // Normalize raw nbformat outputs (mime-keyed dicts with no `type`)
        // first so the trailing auto_dashboard_render cell's dashboard is
        // recognized + rendered as a dashboard preview instead of falling
        // back to its "<IPython…HTML object>" / stdout text.
        normalizeAlertOutputs(
          d.outputs as AlertCellOutput[] | undefined,
        ) as AlertCellOutput[] | undefined,
      ) as AlertCellOutput[] | undefined,
    [d.outputs],
  );

  // Dashboard-KING detection. When a cell has a dashboard output we show
  // ONLY the dashboard — no rotation, no tabs, no distractions. Rule
  // applies everywhere (both /alerts and /chat-in-alert-mode) so the
  // final "dashboard preview" node reads consistently. Hero nodes
  // ALWAYS act as if they have a dashboard (their hoist into the right
  // column is already conditioned on having one).
  const hasDashboard = useMemo(
    () => (sortedOutputs || []).some((o) => isDashboardOutput(o)),
    [sortedOutputs],
  );
  // Honor the explicit flag from AlertCanvas (hero node always sets it).
  // Falls back to the heuristic (any dashboard or any hero) for nodes
  // built outside the /alerts page flow (e.g. /chat alert-mode).
  const forceDashboardOnly =
    d.forceDashboardOnly === true || hasDashboard || isHero;

  useEffect(() => {
    const rawOutputs = Array.isArray(d.outputs) ? d.outputs : [];
    const sorted = Array.isArray(sortedOutputs) ? sortedOutputs : [];
    const dashIdx = sorted.findIndex((o) => isDashboardOutput(o));
    alertLog("node:mount_or_update", {
      sessionId: d.sessionId,
      cellIndex: d.cellIndex,
      displayIndex: d.displayIndex,
      status: d.status,
      hasAlertSend: d.hasAlertSend,
      purposeSnippet: (d.purpose || "").slice(0, 80),
      raw_outputs: rawOutputs.map(summarizeOutput),
      sorted_outputs: sorted.map(summarizeOutput),
      dashboard_index_in_sorted: dashIdx,
      hero_kind:
        sorted.length === 0
          ? "code_or_purpose_fallback"
          : dashIdx >= 0
            ? "dashboard"
            : "last_output",
      // The three flags that control whether the carousel auto-rotates.
      // If any of these is `true` on a non-hero card, rotation is killed.
      // We log them here so a single file sweep will tell us which card
      // is wrongly opting out of rotation.
      isAlertsRoute: Boolean(d.isAlertsRoute),
      isHeroNodeFlag: Boolean(d.isHeroNode),
      explicitForceDashboardOnlyProp: d.forceDashboardOnly === true,
      hasDashboard,
      computedForceDashboardOnly: forceDashboardOnly,
      computedNoRotate: !Boolean(d.isAlertsRoute) || forceDashboardOnly,
      carouselPausedProp: Boolean(d.carouselPaused),
      viewerIsOwner: Boolean(d.viewerIsOwner),
      viewerIsSubscribed: Boolean(d.viewerIsSubscribed),
    });
  }, [
    d.sessionId,
    d.cellIndex,
    d.displayIndex,
    d.status,
    d.hasAlertSend,
    d.purpose,
    d.outputs,
    d.isAlertsRoute,
    d.isHeroNode,
    d.forceDashboardOnly,
    d.carouselPaused,
    d.viewerIsOwner,
    d.viewerIsSubscribed,
    sortedOutputs,
    hasDashboard,
    forceDashboardOnly,
  ]);

  const onClick = () => {
    // Signal hero "Final Results" node → route to the Dashboard tab (the
    // signal performance view) instead of opening the cell modal. The card
    // shown on the node is a preview of that dashboard.
    if (isSignalHero) {
      const params = new URLSearchParams(searchParams?.toString() ?? "");
      params.set("tab", "dashboard");
      alertLog("node:signal_hero_to_dashboard", {
        sessionId: d.sessionId,
        cellIndex: d.cellIndex,
      });
      router.push(`${pathname}?${params.toString()}`, { scroll: false });
      return;
    }

    // Paywall gate: non-owner + non-subscriber on the /alerts route goes
    // to the paywall BEFORE the modal. We explicitly allow the hero
    // ("Final Results") node to open its modal even for non-subscribers,
    // because the snapshot HTML overlay there is the DELAYED teaser the
    // backend already served them — the expand just lets them see it
    // bigger. Blocking that would be customer-hostile.
    if (needsPaywall && !isHero) {
      alertLog("node:click_paywall_intercept", {
        sessionId: d.sessionId,
        cellIndex: d.cellIndex,
        displayIndex: d.displayIndex,
      });
      openPaywall();
      return;
    }

    alertLog("node:click_to_open_modal", {
      sessionId: d.sessionId,
      cellIndex: d.cellIndex,
      displayIndex: d.displayIndex,
      status: d.status,
      sorted_outputs: (sortedOutputs || []).map(summarizeOutput),
      isHero,
    });
    openModal({
      cellIndex: d.cellIndex,
      displayIndex: d.displayIndex,
      purpose: d.purpose,
      code: d.code || d.codePreview || "",
      stdout: d.stdout || "",
      status: d.status,
      hasAlertSend: d.hasAlertSend,
      outputs: sortedOutputs,
      sessionId: d.sessionId,
      // ALL nodes open straight on the "output" tab — user asked for the
      // visual (plotly / dashboard / chart) to be the first thing they
      // see every time, not the Summary tab. AlertCellOutputsView then
      // picks the best inner tab (dashboard > chart > print) so the
      // plotly / heatmap / table is visible immediately. Summary + Code
      // tabs remain accessible for anyone who wants them.
      initialTab: "output",
      // Pass the pre-generated rich description through so the modal's
      // Summary tab can skip the /alerts/explain-cell fetch entirely
      // when it's already available. Kills the "Writing a plain-English
      // summary…" loader on every modal open.
      descriptionMd: d.descriptionMd ?? null,
    });
  };

  const activeRing = selected || isRunning;
  // Brighter, whiter borders for more contrast per user feedback.
  const ringClass = isSuccess
    ? "ring-emerald-300/50"
    : isError
      ? "ring-red-300/50"
      : activeRing
        ? d.viewMode === "signal" ? "ring-emerald-200/55" : "ring-sky-200/55"
        : "ring-slate-200/35";
  const glowClass = isRunning
    ? d.viewMode === "signal" ? "shadow-[0_0_32px_-4px_rgba(110,231,183,0.55),0_8px_24px_-8px_rgba(0,0,0,0.6)]" : "shadow-[0_0_32px_-4px_rgba(186,230,253,0.55),0_8px_24px_-8px_rgba(0,0,0,0.6)]"
    : isSuccess
      ? "shadow-[0_0_20px_-4px_rgba(167,243,208,0.35),0_8px_24px_-8px_rgba(0,0,0,0.55)]"
      : isError
        ? "shadow-[0_0_20px_-4px_rgba(248,113,113,0.35),0_8px_24px_-8px_rgba(0,0,0,0.55)]"
        : "shadow-[0_8px_28px_-10px_rgba(0,0,0,0.7)]";
  const borderClass = isSuccess
    ? "border-emerald-300/50"
    : isError
      ? "border-red-300/50"
      : activeRing
        ? d.viewMode === "signal" ? "border-emerald-200/55" : "border-sky-200/55"
        : "border-slate-200/30";

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2, scale: isHero ? 1.008 : 1.012 }}
      whileTap={{ scale: 0.985 }}
      className={cn(
        "group relative text-left outline-none",
        "rounded-xl overflow-hidden",
        "bg-gradient-to-br from-slate-900/70 via-slate-950 to-slate-950",
        "border transition-colors duration-200",
        borderClass,
        d.viewMode === "signal" ? "hover:border-emerald-300/55 focus-visible:border-emerald-300/60" : "hover:border-sky-300/55 focus-visible:border-sky-300/60",
        "ring-1 ring-inset",
        ringClass,
        glowClass,
        isHero && (d.viewMode === "signal" ? "shadow-[0_0_60px_-12px_rgba(110,231,183,0.35),0_20px_60px_-20px_rgba(0,0,0,0.8)]" : "shadow-[0_0_60px_-12px_rgba(125,211,252,0.35),0_20px_60px_-20px_rgba(0,0,0,0.8)]"),
      )}
      style={
        isHero
          ? {
              width: 720,
              minHeight: 600,
              // Hero drag performance is now handled by rendering the
              // dashboard inside an iframe (see DashboardHeroView). From
              // the parent DOM's perspective the iframe is a single
              // compositing layer, so React Flow's transform on drag only
              // retranslates one rectangle. Previous `will-change`/`contain`
              // hints on this node are no longer needed. If drag perf ever
              // regresses, re-add them here rather than in the iframe.
            }
          : { width: 380, maxHeight: DEFAULT_NODE_MAX_HEIGHT }
      }
      aria-label={`Alert step ${d.displayIndex}: ${d.purpose || "untitled"}. Click to view details.`}
      // Stable hooks for the onboarding-buddy tour to anchor a
      // spotlight + click_target onto a specific node by its 1-based
      // alert-relative position (NOT the notebook cell index, which
      // varies based on whether a research turn preceded the alert).
      // `data-display-index` lets a beat target "the first alert
      // step" with a selector like
      //   [data-testid="alert-canvas-node"][data-display-index="1"]
      // without having to know the underlying cell.id at seed time.
      data-testid="alert-canvas-node"
      data-display-index={d.displayIndex}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-transparent !border-0 !w-2 !h-2"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-transparent !border-0 !w-2 !h-2"
        isConnectable={false}
      />

      {/* Thin top highlight line — stronger on the hero node. */}
      <div
        className={cn(
          "absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent",
          isHero ? (d.viewMode === "signal" ? "via-emerald-200/70" : "via-sky-200/70") : (d.viewMode === "signal" ? "via-emerald-300/45" : "via-sky-300/45"),
        )}
      />

      {/* ── HEADER ROW ──────────────────────────────────────────────── */}
      <div
        className={cn(
          "relative z-[1] flex items-center gap-2.5",
          isHero ? "px-5 pt-4 pb-3" : "px-4 pt-3 pb-2",
        )}
      >
        <div
          className={cn(
            "relative shrink-0 rounded-md flex items-center justify-center border",
            d.viewMode === "signal" ? "border-emerald-300/30 bg-emerald-300/[0.09]" : "border-sky-300/30 bg-sky-300/[0.09]",
            isHero ? "w-[26px] h-[26px]" : "w-[22px] h-[22px]",
          )}
        >
          <span
            className={cn(
              "font-bold tabular-nums leading-none",
              d.viewMode === "signal" ? "text-emerald-100" : "text-sky-100",
              isHero ? "text-[12px]" : "text-[10.5px]",
            )}
          >
            {d.displayIndex}
          </span>
          {isRunning && (
            <motion.span
              className="absolute inset-0 rounded-md"
              animate={{
                boxShadow: [
                  "0 0 0 0 rgba(186,230,253,0.45)",
                  "0 0 0 7px rgba(186,230,253,0)",
                ],
              }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "font-semibold text-slate-50 leading-tight truncate",
              isHero ? "text-[15px]" : "text-[13.5px]",
            )}
          >
            {d.purpose || "Untitled step"}
          </div>
          {isHero && (
            <div className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-emerald-300/90 flex items-center gap-1.5">
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inset-0 rounded-full bg-emerald-400/80 animate-ping" />
                <span className="relative rounded-full bg-emerald-400 w-1.5 h-1.5" />
              </span>
              Dashboard preview
            </div>
          )}
        </div>

        <StatusIcon status={d.status} />
      </div>

      {/* ── PREVIEW — icon-tab round-robin on /alerts; static best-view
          elsewhere. When this cell has a dashboard output (or is a hero
          node) we force the carousel into dashboard-only mode so there's
          NO rotation and NO tabs — the dashboard owns the card.       ── */}
      <div
        className={cn(
          "relative z-[1]",
          isHero ? "mx-5" : "mx-4",
        )}
      >
        <AlertCellCarousel
          purpose={d.purpose}
          description={d.descriptionMd ?? null}
          code={d.codePreview || d.code || ""}
          stdout={d.stdout}
          outputs={sortedOutputs}
          status={d.status}
          paused={Boolean(d.carouselPaused)}
          phaseIndex={Math.max(0, d.displayIndex - 1)}
          noRotate={!isAlertsRoute || forceDashboardOnly}
          showTabs={!forceDashboardOnly}
          forceDashboardOnly={forceDashboardOnly}
          variant={isHero ? "hero" : "default"}
          onExpand={onClick}
          // Ping-pong the hero dashboard vertically on /alerts so the
          // "Final Results" card doesn't sit dead next to the tab-rotating
          // non-dashboard cards. /chat alert-mode stays static.
          autoScrollDashboard={isAlertsRoute}
          viewMode={d.viewMode}
        />
      </div>

      {/* ── FOOTER ROW — status chip only; the expand button now lives in
          the tab bar on the right of the TabBar. This keeps the footer
          quiet so the cell identity (sends email / computation) is the
          dominant secondary line.                                      ── */}
      <div
        className={cn(
          "relative z-[1] flex items-center gap-2",
          isHero ? "px-5 pt-3 pb-3.5" : "px-4 pt-2 pb-2.5",
        )}
      >
        {d.hasAlertSend ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[10px] font-semibold",
              d.viewMode === "signal" ? EMERALD.chipBg : ICY.chipBg,
              d.viewMode === "signal" ? EMERALD.chipText : ICY.chipText,
            )}
          >
            <Bell className="w-2.5 h-2.5" />
            sends email
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[10.5px] text-slate-300/85">
            <Cpu className={`w-2.5 h-2.5 ${d.viewMode === "signal" ? "text-emerald-200/80" : "text-sky-200/80"}`} strokeWidth={2} />
            computation step
          </span>
        )}
      </div>
    </motion.button>
  );
}

export const AlertCellNode = memo(AlertCellNodeComponent);
